import { useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import clsx from 'clsx'
import {
  BarChart3, ClipboardList, FileText, Inbox, Lightbulb,
  FileBarChart, FolderLock, Gavel, GraduationCap, LayoutDashboard, LogOut, Menu, MessageSquare,
  PhoneCall, Scale, Settings, Shield, Users, X, ChevronDown, ShieldAlert, Moon, Sun,
} from 'lucide-react'
import { useAuth } from '../../lib/auth'
import { useI18n } from '../../lib/i18n'
import { useTheme } from '../../lib/theme.jsx'
import { initials } from '../../lib/format'
import { IconButton } from '../ui/Button'

/**
 * Application shell.
 *
 * Sidebar on desktop, slide-over drawer on mobile — the adaptive-navigation
 * rule: at ≥1024px a persistent sidebar costs nothing and keeps the whole
 * structure visible; below that it would eat the screen.
 *
 * Navigation is built from permissions, so nobody is shown a link that will
 * refuse them. For the confidential modules it is built from committee
 * membership, mirroring the server's own rule.
 */

export default function AppShell() {
  const { user, tenant, signOut, can, hasModule, isIccMember, isWhistleblowerMember, hasDisciplinaryCase } = useAuth()
  const { t } = useI18n()
  const location = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Close the drawer on navigation. Leaving it open over the page someone just
  // navigated to is a small thing that feels broken every single time.
  useEffect(() => { setDrawerOpen(false) }, [location.pathname])

  const sections = useMemo(
    () => buildNavigation({ t, can, hasModule, isIccMember, isWhistleblowerMember, hasDisciplinaryCase }),
    [t, can, hasModule, isIccMember, isWhistleblowerMember, hasDisciplinaryCase],
  )

  return (
    <div className="h-dvh overflow-hidden bg-[var(--color-surface-sunken)]">
      {/* Keyboard users should not have to tab through the whole sidebar on
          every page to reach the content. */}
      <a href="#main" className="skip-link rounded bg-[var(--color-accent)] px-3 py-2 text-sm text-white">
        Skip to main content
      </a>

      {/* ── Desktop sidebar ───────────────────────────────────────────────── */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-[var(--color-line)] bg-[var(--color-surface)] lg:flex">
        <Brand tenant={tenant} />
        <Navigation sections={sections} />
        <UserPanel user={user} onSignOut={signOut} />
      </aside>

      {/* ── Mobile drawer ─────────────────────────────────────────────────── */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />

          <aside
            className="relative flex h-full w-[17rem] flex-col border-r border-[var(--color-line)] bg-[var(--color-surface)]"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <div className="flex items-center justify-between border-b border-[var(--color-line)] pr-2">
              <Brand tenant={tenant} bordered={false} />
              <IconButton icon={X} label="Close navigation" onClick={() => setDrawerOpen(false)} />
            </div>

            <Navigation sections={sections} />
            <UserPanel user={user} onSignOut={signOut} />
          </aside>
        </div>
      )}

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <div className="flex h-full flex-col lg:pl-60">
        <TopBar tenant={tenant} onOpenDrawer={() => setDrawerOpen(true)} />

        {/*
         * The scroll container for the whole app — not the document body.
         *
         * This is what lets an individual page opt into filling the space
         * below its own header/filters (h-full flex flex-col, then flex-1
         * min-h-0 on whatever should stretch) so its table can scroll inside
         * a fixed-height card instead of growing the page. A page that does
         * not opt in behaves exactly as before: content taller than the
         * viewport scrolls here, same as it used to scroll on <body>.
         *
         * Full width, with padding that grows rather than a centred column.
         * The cap this replaced was 86rem, which on the 1920px screens in the
         * HR office left a third of the display empty while the grievance
         * queue scrolled horizontally inside it. Casework here is tables and
         * case files side by side, and both want the room. Padding steps up
         * with the viewport instead: tight enough on a phone that a card is
         * not a sliver, generous on a monitor so content is not jammed
         * against the chrome.
         */}
        <main
          id="main"
          // min-h-0: without it, a flex item's default min-height is "auto",
          // which means it refuses to shrink below its own content's natural
          // size — so a page with a big table grew <main> to fit the table
          // instead of <main> staying pinned at flex-1 and letting the table
          // scroll inside itself. This is what actually makes flex-1 mean
          // "fill the remaining space, no more, no less" rather than "grow
          // to whatever the content wants".
          className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-5 sm:py-5 lg:px-7 lg:py-6 2xl:px-10"
        >
          <Outlet />
        </main>
      </div>
    </div>
  )
}

/**
 * The sidebar header: the client's own logo, and nothing else.
 *
 * The company name and "Grievance Portal" label used to live here too, but
 * that duplicated what the top bar now shows on every screen, and squeezed a
 * long legal name into a 15rem-wide column where it just truncated. A
 * tenant with no logo uploaded yet falls back to an initials badge, so the
 * sidebar is never simply blank before a client has got around to Settings.
 */
function Brand({ tenant, bordered = true }) {
  return (
    <div className={clsx('flex h-14 items-center justify-center px-4', bordered && 'border-b border-[var(--color-line)]')}>
      {tenant?.logo_url ? (
        <img
          src={tenant.logo_url}
          alt={tenant.name ?? 'Company logo'}
          className="max-h-9 max-w-full object-contain"
        />
      ) : (
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm font-bold text-white"
          style={{ backgroundColor: 'var(--color-accent)' }}
          aria-hidden="true"
        >
          {tenant?.name ? initials(tenant.name) : 'GP'}
        </div>
      )}
    </div>
  )
}

function Navigation({ sections }) {
  const location = useLocation()

  return (
    <nav className="flex-1 overflow-y-auto scroll-slim px-2.5 py-3" aria-label="Main navigation">
      {sections.map((section, index) => (
        <div key={section.label ?? `section-${index}`} className="mb-4 last:mb-0">
          {section.label && (
            <p className="mb-1 px-2.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-[var(--color-ink-400)]">
              {section.label}
            </p>
          )}

          <ul className="space-y-0.5">
            {section.items.map((item) =>
              item.subItems ? (
                <NavGroup key={item.label} group={item} pathname={location.pathname} />
              ) : (
                <li key={item.to}>
                  <NavItem item={item} />
                </li>
              ),
            )}
          </ul>
        </div>
      ))}
    </nav>
  )
}

/**
 * A collapsible group of related destinations.
 *
 * Grievances and disciplinary cases are two halves of the same job — a
 * complaint by a worker and a proceeding against one — and an officer moves
 * between them constantly. Grouping them says that, where four sibling entries
 * in a flat list would not.
 *
 * The group holding the current page is always open. A person who navigated
 * into a section and then finds its parent collapsed on the next render has to
 * work out where they are, which is the opposite of what navigation is for.
 */
function NavGroup({ group, pathname }) {
  const containsActive = group.subItems.some((sub) => pathname.startsWith(sub.to))

  /*
   * Collapse state is remembered per group, and only as a preference.
   *
   * Written to sessionStorage rather than localStorage: someone collapsing a
   * section is usually clearing space for the task in front of them, not
   * setting it for good. It survives a refresh and forgets by tomorrow.
   */
  const [open, setOpen] = useState(() => {
    if (containsActive) return true

    try {
      return sessionStorage.getItem(`nav.${group.key}`) !== 'closed'
    } catch {
      // Private browsing, or storage refused. The default is open.
      return true
    }
  })

  // Follow the route. Navigating into a collapsed group opens it.
  useEffect(() => {
    if (containsActive) setOpen(true)
  }, [containsActive])

  function toggle() {
    const next = !open
    setOpen(next)

    try {
      sessionStorage.setItem(`nav.${group.key}`, next ? 'open' : 'closed')
    } catch {
      // Preference lost, navigation unaffected.
    }
  }

  return (
    <li>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={clsx(
          'flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors duration-150',
          containsActive && !open
            ? 'font-medium text-[var(--color-accent-text)]'
            : 'text-[var(--color-ink-700)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink-900)]',
        )}
      >
        <group.icon size={16} className="shrink-0" aria-hidden="true" />
        <span className="truncate">{group.label}</span>

        <ChevronDown
          size={14}
          aria-hidden="true"
          className={clsx('ml-auto shrink-0 transition-transform duration-150', !open && '-rotate-90')}
        />
      </button>

      {open && (
        <ul className="mt-0.5 space-y-0.5 border-l border-[var(--color-line)] pl-2.5 ml-[1.3rem]">
          {group.subItems.map((sub) => (
            <li key={sub.to}>
              <NavItem item={sub} nested />
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

function NavItem({ item, nested = false }) {
  const location = useLocation()

  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={() => {
        /*
         * NavLink's own prefix match can't express "active for this path and
         * its children, but NOT for that one sibling that happens to share
         * the prefix" — which is exactly the shape of /grievances (the queue)
         * versus /grievances/assigned (a sibling entry, not a drill-down of
         * the queue). exclude lets a route opt specific paths out of an
         * otherwise-prefix-matched item without losing the match for genuine
         * children like /grievances/:id.
         */
        const isActive = item.end
          ? location.pathname === item.to
          : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
        const excluded = item.exclude?.some((path) => location.pathname === path)

        return clsx(
          'flex items-center gap-2.5 rounded-md px-2.5 text-sm transition-colors duration-150',
          // Sub-items sit a little tighter than top-level ones, so a group
          // reads as one block rather than as more entries competing with it.
          nested ? 'py-1.5' : 'py-2',
          isActive && !excluded
            ? 'bg-[var(--color-accent-soft)] font-medium text-[var(--color-accent-text)]'
            : 'text-[var(--color-ink-700)] hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink-900)]',
        )
      }}
    >
      <item.icon size={nested ? 15 : 16} className="shrink-0" aria-hidden="true" />
      <span className="truncate">{item.label}</span>

      {item.badge > 0 && (
        <span className="ml-auto rounded-full bg-[var(--color-danger)] px-1.5 py-0.5 text-[0.625rem] font-semibold tnum text-white">
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
    </NavLink>
  )
}

function UserPanel({ user, onSignOut }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative border-t border-[var(--color-line)] p-2.5">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-[var(--color-surface-sunken)]"
      >
        {user?.employee?.photo_url ? (
          <img
            src={user.employee.photo_url}
            alt=""
            aria-hidden="true"
            className="h-8 w-8 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-soft)] text-xs font-semibold text-[var(--color-accent-text)]"
            aria-hidden="true"
          >
            {initials(user?.name)}
          </span>
        )}

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-[var(--color-ink-900)]">
            {user?.name}
          </span>
          <span className="block truncate text-xs text-[var(--color-ink-500)]">
            {user?.roles?.[0]?.replace(/_/g, ' ') ?? 'Employee'}
          </span>
        </span>

        <ChevronDown
          size={14}
          className={clsx('shrink-0 text-[var(--color-ink-400)] transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="absolute bottom-full left-2.5 right-2.5 mb-1 overflow-hidden rounded-md border border-[var(--color-line)] bg-[var(--color-surface)] shadow-lg">
          <NavLink
            to="/settings/profile"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--color-ink-700)] hover:bg-[var(--color-surface-sunken)]"
          >
            <Settings size={14} aria-hidden="true" />
            My profile
          </NavLink>

          {/* Sign out is separated from the rest — the destructive-separation
              rule; it is the one item here nobody should hit by accident. */}
          <button
            type="button"
            onClick={onSignOut}
            className="flex w-full cursor-pointer items-center gap-2 border-t border-[var(--color-line)] px-3 py-2 text-left text-sm text-[var(--color-danger)] hover:bg-[var(--color-danger-bg)]"
          >
            <LogOut size={14} aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}

function TopBar({ tenant, onOpenDrawer }) {
  const { locale, setLocale, locales } = useI18n()
  const { theme, toggle } = useTheme()

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-[var(--color-line)] bg-[var(--color-surface)]/95 px-3 backdrop-blur sm:px-5 lg:px-7 2xl:px-10">
      <div className="lg:hidden">
        <IconButton icon={Menu} label="Open navigation" onClick={onOpenDrawer} />
      </div>

      {/* The company name moved here from the sidebar, which now shows only
          the logo — one place for the name, readable on every screen rather
          than truncated in a fixed-width sidebar column. */}
      <div className="min-w-0">
        <p className="truncate font-display text-sm font-semibold leading-tight text-[var(--color-ink-900)]">
          {tenant?.name ?? 'Grievance Portal'}
        </p>
        <p className="truncate text-[0.6875rem] leading-tight text-[var(--color-ink-500)]">
          Grievance Portal
        </p>
      </div>

      <div className="flex-1" />

      <IconButton
        icon={theme === 'dark' ? Sun : Moon}
        label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        onClick={toggle}
        className="bg-transparent hover:bg-transparent"
      />

      {/* Language is in the top bar rather than buried in settings: a worker
          who cannot read the interface cannot navigate to the setting that
          would fix it. */}
      <label className="sr-only" htmlFor="locale-select">Language</label>
      <select
        id="locale-select"
        value={locale}
        onChange={(event) => setLocale(event.target.value)}
        className="h-9 cursor-pointer rounded-md border border-[var(--color-line-strong)] bg-[var(--color-surface)] px-2 text-sm text-[var(--color-ink-700)]"
      >
        {locales.map((option) => (
          <option key={option.code} value={option.code}>{option.native}</option>
        ))}
      </select>
    </header>
  )
}

/**
 * Build the navigation from what this person can actually reach.
 *
 * Hiding an unreachable link is not security — the server decides — but showing
 * one is a usability failure: a menu entry that always 403s teaches people the
 * product is unreliable.
 */
function buildNavigation({ t, can, hasModule, isIccMember, isWhistleblowerMember, hasDisciplinaryCase }) {
  const sections = []

  // ── Personal ────────────────────────────────────────────────────────────
  const mine = [
    { to: '/', label: t('nav.dashboard'), icon: BarChart3, end: true },
    { to: '/my/grievances', label: t('nav.myGrievances'), icon: FileText },
  ]

  if (hasModule('feedback')) {
    mine.push({ to: '/suggestions', label: t('nav.suggestionBox'), icon: Lightbulb })
  }

  /*
   * Only for someone who actually has proceedings against them.
   *
   * A standing "Discipline" entry in an employee's own menu, shown to a
   * workforce that has overwhelmingly done nothing wrong, reads as a threat
   * rather than a feature. It appears when there is something to see and
   * disappears again when there is not.
   */
  if (hasDisciplinaryCase) {
    mine.push({ to: '/my/disciplinary', label: t('nav.myDiscipline'), icon: Gavel })
  }

  sections.push({ label: null, items: mine })

  // ── Casework ────────────────────────────────────────────────────────────
  const casework = []

  /*
   * Grievance and discipline as one group.
   *
   * They are two halves of the same job — a complaint by a worker and a
   * proceeding against one — and an officer moves between them constantly. Four
   * siblings in a flat list say nothing about that relationship; a group does.
   *
   * The sub-items are built first and the group is only added if any survived,
   * so somebody with a single permission gets one entry rather than a group
   * wrapping one link.
   */
  const grievanceItems = []

  if (hasModule('grievance')) {
    if (can('GRV-01', 'GRV-07')) {
      // Prefix-matched (so opening a case from the queue at /grievances/:id
      // still shows the queue as active), but with /grievances/assigned
      // explicitly excluded — that path is a sibling entry of its own below,
      // not a drill-down of the queue, and without this exclusion both lit up
      // together purely because one URL happens to start with the other's.
      grievanceItems.push({ to: '/grievances', label: t('nav.queue'), icon: Inbox, exclude: ['/grievances/assigned'] })
    }

    grievanceItems.push({ to: '/grievances/assigned', label: t('nav.assignedToMe'), icon: ClipboardList })

    if (can('GRV-06')) {
      grievanceItems.push({ to: '/enquiries', label: t('nav.enquiries'), icon: PhoneCall })
    }

    // Behind its own permission: reading grievances does not imply reading
    // proceedings against colleagues.
    if (can('DSC-01')) {
      grievanceItems.push({ to: '/disciplinary', label: t('nav.discipline'), icon: Gavel })
    }
  }

  if (grievanceItems.length > 1) {
    casework.push({
      key: 'grievance-discipline',
      label: t('nav.grievanceDiscipline'),
      icon: Scale,
      subItems: grievanceItems,
    })
  } else if (grievanceItems.length === 1) {
    casework.push(grievanceItems[0])
  }

  if (hasModule('feedback') && can('FBK-01')) {
    casework.push({ to: '/feedback', label: t('nav.feedback'), icon: MessageSquare })
  }

  if (hasModule('survey') && can('SRV-01')) {
    casework.push({ to: '/surveys', label: t('nav.surveys'), icon: ClipboardList })
  }

  if (casework.length) sections.push({ label: t('nav.sectionCasework'), items: casework })

  /*
   * Confidential.
   *
   * Gated on committee membership, not on permission — the same rule the
   * server applies. An administrator who granted themselves POSH-01 would
   * still see nothing here, and would still be refused if they typed the URL.
   *
   * These entries are styled like every other item on purpose. They were once
   * tinted violet to mark the boundary before you clicked, which worked while
   * POSH was a single row — but as a group of five it became seven coloured
   * rows, and a signal that covers a third of the sidebar has stopped being a
   * signal. The boundary is still stated where it counts: the CONFIDENTIAL
   * heading above them, and the banner across the top of every screen inside.
   */
  const confidential = []

  /*
   * POSH as a group, mirroring how the Act divides the work.
   *
   * Dashboard, roster, complaints, training and the annual return are five
   * distinct obligations with five different deadlines, and a Presiding Officer
   * moves between them constantly. One entry hiding all five behind tabs made
   * the Section 21 return in particular easy to forget until it was late.
   */
  if (hasModule('posh') && isIccMember) {
    confidential.push({
      key: 'posh',
      label: t('nav.posh'),
      icon: Shield,
      subItems: [
        { to: '/posh/dashboard', label: t('nav.poshDashboard'), icon: LayoutDashboard },
        { to: '/posh/icc', label: t('nav.iccRoster'), icon: Users },
        { to: '/posh/complaints', label: t('nav.poshComplaints'), icon: FolderLock },
        { to: '/posh/training', label: t('nav.poshTraining'), icon: GraduationCap },
        { to: '/posh/annual-report', label: t('nav.poshAnnualReport'), icon: FileBarChart },
      ],
    })
  } else if (hasModule('posh') && can('ADM-03')) {
    // A brand-new tenant has nobody on the Committee yet, and every other
    // route into this module requires already being a serving member — the
    // one deliberate exception is appointing the very first one. Without
    // this link an administrator has no way to discover /posh/icc exists at
    // all. The roster screen itself decides whether appointing is actually
    // still possible; this only offers the door.
    confidential.push({ to: '/posh/icc', label: t('nav.iccRoster'), icon: Shield })
  }

  if (hasModule('whistleblower') && isWhistleblowerMember) {
    confidential.push({
      to: '/whistleblower', label: t('nav.whistleblower'), icon: ShieldAlert,
    })
  } else if (hasModule('whistleblower') && can('ADM-03')) {
    // Same bootstrap reasoning as POSH above.
    confidential.push({ to: '/whistleblower/roster', label: t('nav.whistleblower'), icon: ShieldAlert })
  }

  if (confidential.length) sections.push({ label: t('nav.sectionConfidential'), items: confidential })

  // ── Administration ──────────────────────────────────────────────────────
  const admin = []

  if (can('RPT-01')) admin.push({ to: '/reports', label: t('nav.reports'), icon: BarChart3 })
  if (can('ORG-01')) admin.push({ to: '/people', label: t('nav.people'), icon: Users })

  if (can('ADM-01', 'ADM-03', 'GRV-04')) {
    // /settings/profile is "My profile" (its own sidebar link, opened from the
    // user panel), not a drill-down of Settings — excluded so the two don't
    // highlight together, same reasoning as /grievances/assigned below.
    admin.push({ to: '/settings', label: t('nav.settings'), icon: Settings, exclude: ['/settings/profile'] })
  }

  if (admin.length) sections.push({ label: t('nav.sectionAdmin'), items: admin })

  return sections
}
